import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { Modal, ModalBody } from 'reactstrap';
import Select, { components as selectComponents } from 'react-select';
import CustomButton from '../OnboardingComponents/CustomButton';
import CollaboratorSearchSelect, {
  getCollaboratorUserId,
  collabSelectStyles,
  isUuid,
} from '../DashboardComponents/CollaboratorSearchSelect';
import { Avatar } from '../DashboardComponents/AvatarGroup';
import { ICONS } from '../../constants/icons';
import { get } from '../../api/axios';
import endpoints from '../../api/endpoints';
import { getUserAvatarUrl } from '../../helpers/userAvatar';
import { getProjectCollaborators } from '../../helpers/session';

const mapPersonToOption = person => {
  const userId = person?.userId || person?.user_id || person?.id || '';
  const name = person?.name || 'Collaborator';
  const email = person?.email || person?.user?.email || '';

  return {
    value: userId ? String(userId) : '',
    label: email ? `${name} (${email})` : name,
    name,
    email,
    avatar: person?.avatar || person?.src || getUserAvatarUrl(person?.user),
    raw: person?.user || { id: userId },
  };
};

const mapMemberToOption = member => {
  const user = member?.user || {};
  const userId =
    member?.user_id || member?.userId || user.id || user.user_id || '';
  const name =
    member?.name ||
    `${user.first_name || ''} ${user.last_name || ''}`.trim() ||
    user.full_name ||
    user.email ||
    'Collaborator';
  const email = user.email || member?.email || '';

  return {
    value: userId ? String(userId) : '',
    label: email ? `${name} (${email})` : name,
    name,
    email,
    avatar: member?.avatar || getUserAvatarUrl(user),
    // Store the user object (not the invitation/membership row id).
    raw: Object.keys(user).length ? user : { id: userId },
  };
};

const CollaboratorOption = props => {
  const { data } = props;
  return (
    <selectComponents.Option {...props}>
      <span className="project-collab-modal__option">
        <Avatar
          src={data.avatar}
          name={data.name}
          size="xs"
          className="project-collab-modal__option-avatar"
        />
        <span className="project-collab-modal__option-meta">
          <span className="project-collab-modal__option-name">{data.name}</span>
          {data.email ? (
            <span className="project-collab-modal__option-email">{data.email}</span>
          ) : null}
        </span>
      </span>
    </selectComponents.Option>
  );
};

const CollaboratorMultiValueLabel = props => {
  const { data } = props;
  return (
    <selectComponents.MultiValueLabel {...props}>
      <span className="project-collab-modal__chip">
        <Avatar
          src={data.avatar}
          name={data.name}
          size="xs"
          className="project-collab-modal__chip-avatar"
        />
        <span className="project-collab-modal__chip-name">{data.name}</span>
      </span>
    </selectComponents.MultiValueLabel>
  );
};

/**
 * Add collaborators to an existing solo/shadow session.
 * Project sessions: pick from active project members only (API requirement).
 * City sessions: search city users (UUID only).
 */
const AddSessionCollaboratorModal = ({
  isOpen,
  onClose,
  projectId = null,
  project = null,
  cityId = null,
  existingUserIds = [],
  onSubmit,
  isSubmitting = false,
}) => {
  const currentUser = useSelector(state => state.auth?.currentUser);
  const isCitySession = Boolean(cityId) && !projectId;
  const [selected, setSelected] = useState([]);
  const [options, setOptions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [menuOpen, setMenuOpen] = useState(false);

  const existingIdSet = useMemo(
    () =>
      new Set(
        (existingUserIds || [])
          .map(id => String(id || '').trim().toLowerCase())
          .filter(Boolean)
      ),
    [existingUserIds]
  );
  const existingIdsRef = useRef(existingIdSet);
  existingIdsRef.current = existingIdSet;

  const resetState = useCallback(() => {
    setSelected([]);
    setError('');
    setMenuOpen(false);
    setOptions([]);
    setIsLoading(false);
  }, []);

  useEffect(() => {
    if (!isOpen) {
      resetState();
      return undefined;
    }
    return undefined;
  }, [isOpen, resetState]);

  useEffect(() => {
    if (!isOpen || isCitySession || !projectId) return undefined;

    let cancelled = false;

    const loadMembers = async () => {
      setIsLoading(true);
      setError('');

      // Same member source used when starting a shadow session.
      const localPeople = getProjectCollaborators(project, currentUser, {
        excludeCurrentUser: true,
      })
        .map(mapPersonToOption)
        .filter(option => option.value);

      if (localPeople.length > 0) {
        if (!cancelled) {
          setOptions(localPeople);
          setIsLoading(false);
        }
        return;
      }

      try {
        // Session API requires active project members — load members.user, not invitations.
        const response = await get(endpoints.getProjectById(projectId), {
          params: { 'with[]': ['members.user'] },
          skipErrorToast: true,
        });

        if (cancelled) return;

        if (response?.data?.success) {
          const members =
            response.data.result?.members ||
            response.data.result?.collaborators ||
            [];
          const mapped = members
            .filter(member => {
              const status = String(member.status || 'active').toLowerCase();
              return (
                status === 'active' ||
                status === 'accepted' ||
                !member.status
              );
            })
            .map(mapMemberToOption)
            .filter(option => option.value);
          setOptions(mapped);
          if (mapped.length === 0) {
            setError('No active project members available to add.');
          }
        } else {
          setOptions([]);
          setError('Unable to load project members. Please try again.');
        }
      } catch {
        if (!cancelled) {
          setOptions([]);
          setError('Unable to load project members. Please try again.');
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    loadMembers();
    return () => {
      cancelled = true;
    };
  }, [currentUser, isCitySession, isOpen, project, projectId]);

  const availableOptions = useMemo(
    () =>
      options.filter(option => {
        const keys = [option.value, option.email]
          .filter(Boolean)
          .map(value => String(value).trim().toLowerCase());
        return !keys.some(key => existingIdSet.has(key));
      }),
    [existingIdSet, options]
  );

  const handleClose = () => {
    if (isSubmitting) return;
    resetState();
    onClose?.();
  };

  const resolveUserIds = () =>
    Array.from(
      new Set(
        (selected || [])
          .map(item => {
            const fromValue = String(item?.value || '').trim();
            if (fromValue) return fromValue;
            return getCollaboratorUserId(item);
          })
          .filter(id => id && isUuid(id))
          .filter(id => !existingIdsRef.current.has(String(id).toLowerCase()))
      )
    );

  const handleSubmit = async event => {
    event?.preventDefault?.();
    if (isSubmitting) return;

    const userIds = resolveUserIds();
    if (userIds.length === 0) {
      setError(
        projectId
          ? 'Select at least one active project member'
          : 'Select at least one collaborator'
      );
      return;
    }

    setError('');
    const result = await onSubmit?.(userIds);
    const ok = result === true || result?.ok === true;
    if (ok) {
      resetState();
      onClose?.();
      return;
    }

    const message =
      (typeof result === 'object' && result?.message) ||
      'Failed to add collaborators';
    setError(message);
  };

  const unavailableReason = isCitySession
    ? !cityId
      ? 'City is unavailable for this session'
      : null
    : !projectId
      ? 'Project is unavailable for this session'
      : null;

  return (
    <Modal
      isOpen={isOpen}
      toggle={handleClose}
      centered
      zIndex={20020}
      wrapClassName="amok-modal-wrap--above-player"
      container={typeof document !== 'undefined' ? document.body : undefined}
      className="amok-modal amok-modal--shadow-session amok-modal--collab-invite"
      contentClassName="amok-modal__content"
      backdropClassName="amok-modal__backdrop amok-modal-backdrop--blur"
    >
      <ModalBody className="amok-modal__body project-collab-modal__body">
        <form
          className="project-collab-modal project-collab-modal--invite"
          onSubmit={handleSubmit}
          noValidate
        >
          <div className="project-collab-modal__main">
            <div className="project-collab-modal__header">
              <h3 className="project-collab-modal__title">Add Collaborator</h3>
              <p className="project-collab-modal__subtitle">
                {projectId
                  ? 'Choose active project members to add to this session.'
                  : 'Invite people to join this session.'}
              </p>
            </div>

            <div className="project-collab-modal__fields">
              {isCitySession ? (
                <CollaboratorSearchSelect
                  cityId={cityId}
                  value={selected}
                  onChange={next => {
                    const filtered = (next || []).filter(item => {
                      const id =
                        getCollaboratorUserId(item) ||
                        String(item?.value || '').trim();
                      return (
                        id && !existingIdSet.has(String(id).toLowerCase())
                      );
                    });
                    setSelected(filtered);
                    setError('');
                  }}
                  disabled={isSubmitting || Boolean(unavailableReason)}
                  disabledReason={unavailableReason}
                  skipCityCheck={false}
                  error={error || null}
                  enabled={isOpen}
                  inputId="session-add-collab-search"
                  allowCreate={false}
                  label="Search Collaborator"
                  menuZIndex={20040}
                />
              ) : (
                <div className="project-collab-modal__field">
                  <label
                    className="project-collab-modal__label"
                    htmlFor="session-add-collab-project-search"
                  >
                    Search Collaborator
                  </label>
                  <div
                    className={`project-collab-modal__search${
                      error ? ' project-collab-modal__search--error' : ''
                    }`}
                  >
                    <img src={ICONS.messageSearchCollab} alt="" />
                    <Select
                      inputId="session-add-collab-project-search"
                      className="project-collab-modal__user-select"
                      classNamePrefix="project-collab-modal-user"
                      options={availableOptions}
                      value={selected}
                      onChange={next => {
                        setSelected(next || []);
                        setError('');
                      }}
                      isMulti
                      isSearchable
                      isClearable={false}
                      isDisabled={
                        isSubmitting || Boolean(unavailableReason)
                      }
                      isLoading={isLoading}
                      menuIsOpen={menuOpen}
                      onMenuOpen={() => setMenuOpen(true)}
                      onMenuClose={() => setMenuOpen(false)}
                      closeMenuOnSelect={false}
                      hideSelectedOptions
                      filterOption={(option, rawInput) => {
                        const query = String(rawInput || '')
                          .trim()
                          .toLowerCase();
                        if (!query) return true;
                        const data = option.data || {};
                        const haystack =
                          `${option.label} ${data.email || ''}`.toLowerCase();
                        return haystack.includes(query);
                      }}
                      placeholder={
                        unavailableReason ||
                        (isLoading
                          ? 'Loading project members...'
                          : 'Search project members...')
                      }
                      noOptionsMessage={() =>
                        isLoading
                          ? 'Loading...'
                          : unavailableReason ||
                            'No available project members found'
                      }
                      loadingMessage={() => 'Loading...'}
                      components={{
                        Option: CollaboratorOption,
                        MultiValueLabel: CollaboratorMultiValueLabel,
                        IndicatorSeparator: null,
                        DropdownIndicator: null,
                      }}
                      styles={collabSelectStyles(Boolean(error), 20040)}
                      menuPortalTarget={
                        typeof document !== 'undefined' ? document.body : null
                      }
                      menuPosition="fixed"
                      aria-label="Search Collaborator"
                    />
                  </div>
                  {error ? (
                    <span className="project-collab-modal__error">{error}</span>
                  ) : null}
                </div>
              )}
            </div>
          </div>

          <div className="project-collab-modal__actions">
            <CustomButton
              variant="danger"
              size="md"
              type="button"
              onClick={handleClose}
              disabled={isSubmitting}
            >
              Cancel
            </CustomButton>
            <CustomButton
              variant="primary"
              size="md"
              type="submit"
              loading={isSubmitting}
              disabled={isSubmitting || selected.length === 0}
            >
              Add Collaborator
            </CustomButton>
          </div>
        </form>
      </ModalBody>
    </Modal>
  );
};

export default AddSessionCollaboratorModal;
